import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Redirect, Link } from "react-router-dom";

import { withStyles } from "@mui/styles";
import { format } from 'date-fns';

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MapCard from "./MapCard";
import MapCardNoData from "./MapCardNoData";
import BarChartIcon from '@mui/icons-material/BarChart';

import MatchHistoryTable from "./MatchHistoryTable";
import ProfileInfoCard from "./profileInfoCard";
import SortMatchesTable from "./SortMatchesTable";
import PageShell from "../common/PageShell";
import { notifyIfCannotSendFriendRequest, notifyIfTargetCannotReceiveFriendRequest } from "../../utils/friendRequests";
import ProfileTabs from "./ProfileTabs";
import TabPanel from "./TabPanel";
import ProfileSummaryTab from "./ProfileSummaryTab";
import ProfileMatchHistoryTab from "./ProfileMatchHistoryTab";
import ProfileStatsTab from "./ProfileStatsTab";
import ProfileFriendsTab from "./ProfileFriendsTab";

const styles = (theme) => ({
  root: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: 0,

    marginLeft: "auto",
    marginRight: "auto",

    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  homepageRoot: {
    minHeight: 560,
    marginTop: 0,
  },
  button: {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  formGroup: {
    display: "block",
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
  },
  lobbyContainer: {
    display: "flex",
    position: "relative",
  },
  avatar: {
    width: 120,
    height: 120,
    margin: "15px auto",
    position: "relative",
    right: 0,
  },
  lobbyPersonBox: {
    width: 214,
    height: 235,
    marginRight: 20,
    borderRadius: 4,
    border: "solid 1px #adadad",
  },
  inviteLinkBtn: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  containerMapMatchesStats: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: "2.5rem",
    paddingTop: "2rem",
    width: "100%",
  },
  matchmakingGrid: {
    position: "relative",
    display: 'flex',
    flexDirection: 'column',
    width: "100%",
  },
  containerCardWithData: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '1.5rem 3rem',
    width: '100%',
    borderRadius: "4px",
    boxSizing: 'border-box',
    marginBottom: '2rem',
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, 1fr)', 
    },
    '@media (max-width: 450px)': {
      gridTemplateColumns: '1fr', 
    },
  },
  containerCardNoData: {
    position: "relative",
    display: "flex", 
    flexDirection: "row", 
    justifyContent: "center",
    alignItems: "center",
  },
  panel: {
    position: "absolute",
    top: 0,
    left: 0,
    inset: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.50)",
    zIndex: 1,
    display: "flex",             
    alignItems: "center",        
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: "4px",
  },
  containerNoDataText: {
    position: "absolute",
    width: "100%",
    height: "14rem",
    display: "flex",             
    alignItems: "center",        
    justifyContent: "center",
    flexDirection: "column",
    zIndex: 2,
  },
  iconOutline: {
    display: "flex",
    alignItems: "center",        
    justifyContent: "center",
    border: "3.5px solid #ffffff",
    borderRadius: "50%",
    padding: "0.5rem",
    marginBottom: "0.5rem",
  },
  noDataTitle: {
    width: "100%",
    fontSize: "28px",
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: "1px",
  },
  noDataSubtitle: {
    width: "100%",
    fontSize: "16px",
    textAlign: "center",
  },
  matchmakingOptionsCont: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: "center", 
  },
  formatSortTable: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    gap: "1rem",
    width: "100%"
  },
  matchmakingSectionTitle: {
    margin: "0 0 1rem 0",
    width: "100%",
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "left",
  },
  anim: {
    position: "absolute",
    top: 13,
    right: -35,
  },
  statsCont: {
    position: "absolute",
    border: "solid 1px #b5b1b3",
    display: "flex",
    flexWrap: "wrap",
    borderRadius: 4,
    padding: 16,
    top: 0,
    left: 250,
    width: 400,
    height: 204,
  },
  statField: { display: "flex", width: 400 },
  statIcon: { color: "#666666" },
  statTitle: { fontSize: 14, marginLeft: 8, fontWeight: 500, color: "#666666" },
  statValue: { fontSize: 14, marginLeft: 8, display: "flex" },
  profileFlag: { height: 24, display: "flex" },
  vspacer10: {
    height: 10,
  },
  steamProfileLinkButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  faceitProfileLinkButton: {
    position: "absolute",
    top: 57,
    right: 6,
  },
  steamIcon: {
    height: 48,
  },
  faceitIcon: {
    height: 32,
    width: 32,
    background: "#ffffff",
    padding: 5,
    borderRadius: 8,
    border: "solid 1px #666666",
  },
  matchHistoryTable: {
    width: '100%',
    maxWidth: '100%',
  },
  notFoundContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
    width: "100%",
  },
  notFoundTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: 8,
    textAlign: "center",
  },
  notFoundSubtitle: {
    fontSize: "14px",
    color: "#999999",
    marginBottom: 16,
    textAlign: "center",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
    width: "100%",
  },
  loadingText: {
    marginTop: 16,
    fontSize: "16px",
    color: "#666666",
  },
  "@media (min-width: 1024px)": {
    homepageRoot: {
      width: "100%",
      margin: 0,
    },
    formGroup: {
      display: "flex",
      justifyContent: "center",
    },
    lobbyFrame: {
      margin: "20px auto",
      textAlign: "center",
      fontSize: "14pt",
      position: "relative",
      padding: "3.5rem",
      minHeight: 500,
      // width: 1000,
    },
    homeContainer: {
      display: "none",
    },
  },
  "@media (max-width: 1024px)": {
    lobbyFrame: {
      padding: "2.5rem",
    },
  },
  "@media (max-width: 670px)": {
    lobbyFrame: {
      padding: "1.5rem",
    },
  },
});

const MAP_NAMES = [
  "de_mirage",
  "de_inferno",
  "de_nuke",
  "de_anubis",
  "de_vertigo",
  "de_ancient",
  "de_cache",
  "de_cobblestone_2018",
  "de_dust_ii",
];
const SORT_GENERAL_VALUES = ["Date", "Win", "Loss"];
const SORT_MAPS_VALUES = [
  "All Maps",
  "Mirage",
  "Inferno",
  "Nuke",
  "Anubis",
  "Vertigo",
  "Ancient",
  "Cache",
  "Cobblestone",
  "Dust II",
];
class ProfilePage extends React.Component {
  constructor() {
    super();
    this.friendsPollingPid = null;
    this.state = {
      selectedTab: 0,
      friendRequestSubmittingSteamId: null,
    };
  }

  resolveSteamId = (userLike) => {
    return userLike?.steamId || userLike?.steamId64 || userLike?.steam64Id || null;
  };

  componentDidMount() {
    const { id } = this.props.match.params;

    this.props.actions.getProfilePageUserDataByUserId(id);
    const { user } = this.props.catalog || {};
    if (user) {
      const viewerSteamId = this.resolveSteamId(user);
      this.props.actions.getFriends({ userId: user.userId, steamId: viewerSteamId });
      this.props.actions.getLobbyByUserId(user.userId);
      this.startFriendsPolling(user);
      const targetSteamId = this.resolveSteamId(this.props.catalog?.profileData);
      if (targetSteamId) {
        this.props.actions.getProfileRelationship({ viewerSteamId, targetSteamId });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { id: prevId } = prevProps.match.params;
    const { id } = this.props.match.params;
    const prevUser = prevProps.catalog?.user;
    const currUser = this.props.catalog?.user;
    const prevProfileSteamId = this.resolveSteamId(prevProps.catalog?.profileData);
    const currProfileSteamId = this.resolveSteamId(this.props.catalog?.profileData);
    
    if (prevId !== id) {
      this.props.actions.getProfilePageUserDataByUserId(id);
      if (currUser) {
        this.props.actions.getFriends({ userId: currUser.userId, steamId: this.resolveSteamId(currUser) });
      }
    }

    if ((!prevUser && currUser) || (prevUser && currUser && this.resolveSteamId(prevUser) !== this.resolveSteamId(currUser))) {
      const viewerSteamId = this.resolveSteamId(currUser);
      this.props.actions.getFriends({ userId: currUser.userId, steamId: viewerSteamId });
      this.props.actions.getLobbyByUserId(currUser.userId);
      this.stopFriendsPolling();
      this.startFriendsPolling(currUser);
      if (viewerSteamId && currProfileSteamId) {
        this.props.actions.getProfileRelationship({ viewerSteamId, targetSteamId: currProfileSteamId });
      }
    }

    if (currUser && currProfileSteamId && currProfileSteamId !== prevProfileSteamId) {
      const viewerSteamId = this.resolveSteamId(currUser);
      if (viewerSteamId) {
        this.props.actions.getProfileRelationship({ viewerSteamId, targetSteamId: currProfileSteamId });
      }
    }
  }

  componentWillUnmount() {
    this.stopFriendsPolling();
  }

  startFriendsPolling(user) {
    // One-shot fetch only — ongoing updates arrive via Socket.IO push from the lobby
    this.props.actions.getFriends({ userId: user.userId, steamId: this.resolveSteamId(user) });
  }

  stopFriendsPolling() {
    // No-op — polling removed in favour of Socket.IO push
  }

  validateForm = (e) => {
    e.preventDefault();
  };

  handleTabChange = (event, newValue) => {
    this.setState({ selectedTab: newValue });
  };

  handleSortChange = (sortType) => (sortOption) => {
    this.props.actions.setSortOption(sortType, sortOption);
  };

  handleSendRequestFriendFromProfilePage = (steamId) => {
    const { user } = this.props.catalog;
    if (notifyIfCannotSendFriendRequest(user)) {
      return Promise.resolve();
    }
    if (notifyIfTargetCannotReceiveFriendRequest(this.props.catalog?.profileData)) {
      return Promise.resolve();
    }
    if (!steamId) {
      return Promise.resolve();
    }
    const viewerSteamId = this.resolveSteamId(user);
    if (!viewerSteamId) {
      this.props.actions.sendFriendRequestError("Your Steam ID is unavailable. Please try again later.");
      return Promise.resolve();
    }
    this.setState({ friendRequestSubmittingSteamId: steamId });
    return this.props.actions.sendFriendRequest({
      recipientSteamId: steamId,
      userId: user.userId,
      userSteamId: viewerSteamId
    }).finally(() => {
      this.setState((prevState) => (
        prevState.friendRequestSubmittingSteamId === steamId
          ? { friendRequestSubmittingSteamId: null }
          : null
      ));
    });
  };

  handleCancelFriendRequestFromProfilePage = (steamId) => {
    const { user } = this.props.catalog;
    const viewerSteamId = this.resolveSteamId(user);
    if (!viewerSteamId) {
      this.props.actions.sendFriendRequestError("Your Steam ID is unavailable. Please try again later.");
      return;
    }
    this.props.actions.cancelFriendRequest({ 
      recipientSteamId: steamId, 
      userId: user.userId, 
      userSteamId: viewerSteamId 
    });
  };

  handleAcceptFriendRequestFromProfilePage = (steamId) => {
    const { user, profileData } = this.props.catalog;
    const viewerSteamId = this.resolveSteamId(user);
    const targetSteamId = steamId || this.resolveSteamId(profileData);
    if (!viewerSteamId || !targetSteamId) {
      this.props.actions.sendFriendRequestError("Unable to process request right now. Please try again.");
      return Promise.resolve();
    }

    return this.props.actions
      .acceptFriendRequest({
        fromUserSteamId: targetSteamId,
        toUserSteamId: viewerSteamId,
        toUserId: user.userId,
      })
      .then(() =>
        this.props.actions.getProfileRelationship({
          viewerSteamId,
          targetSteamId,
        })
      );
  };

  handleDismissFriendRequestFromProfilePage = (steamId) => {
    const { user, profileData } = this.props.catalog;
    const viewerSteamId = this.resolveSteamId(user);
    const targetSteamId = steamId || this.resolveSteamId(profileData);
    if (!viewerSteamId || !targetSteamId) {
      this.props.actions.sendFriendRequestError("Unable to process request right now. Please try again.");
      return Promise.resolve();
    }

    return this.props.actions
      .declineFriendRequest({
        fromUserSteamId: targetSteamId,
        toUserSteamId: viewerSteamId,
        toUserId: user.userId,
      })
      .then(() =>
        this.props.actions.getProfileRelationship({
          viewerSteamId,
          targetSteamId,
        })
      );
  };

  handleUnfriendFromProfilePage = (steamId) => {
    const { user, profileData } = this.props.catalog;
    const viewerSteamId = this.resolveSteamId(user);
    const targetSteamId = steamId || this.resolveSteamId(profileData);
    if (!viewerSteamId || !targetSteamId) {
      this.props.actions.sendFriendRequestError("Unable to process request right now. Please try again.");
      return Promise.resolve();
    }

    return this.props.actions
      .deleteFriend({
        mySteamId: viewerSteamId,
        friendSteamId: targetSteamId,
        userId: user.userId,
      })
      .then(() =>
        this.props.actions.getProfileRelationship({
          viewerSteamId,
          targetSteamId,
        })
      );
  };

  render() {
    const { classes, actions, catalog } = this.props;
    const { user, profileData = {}, profileDataLoading = false, matchSummary = {}, last25MatchData = {}, mapsStats = {}, recentStats = {}, sortOptions, friendRequestSuccess = null, friendRequestError = null } = catalog;
    const relationship = catalog.profileRelationship || 'none';
    const profileSteamId = this.resolveSteamId(profileData);

    const matchNumber = matchSummary?.totalPlayedMatches ?? "not available";

    const hasPlayedAtLeastOneMatch = Object.keys(last25MatchData || {}).length > 0;

    return (
      <PageShell maxWidth="lg">
        <div className={classes.homepageRoot}>
          <Paper className={classes.lobbyFrame}>
          {!user && <Typography>Please wait...</Typography>}

          {user && (
            <>
              {profileDataLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={40} />
                  <Typography className={classes.loadingText}>Loading...</Typography>
                </div>
              ) : profileData === null ? (
                <div className={classes.notFoundContainer}>
                  <Typography className={classes.notFoundTitle}>Player not found</Typography>
                  <Typography className={classes.notFoundSubtitle}>We couldn't find a player with this ID. Please check the link or search again.</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to="/"
                  >
                    Go to Home
                  </Button>
                </div>
              ) : (
                <>
                  {profileData && matchSummary && (
                    <ProfileInfoCard 
                      profileData={profileData} 
                      matchSummary = {matchSummary}
                      recentStats = {recentStats}
                      isOwnProfile={profileData._id === user._id} 
                      currentUserAvatarSource={user}
                      playerStatus={(function(){
                        if (profileData.isOnline !== true) return 'offline';

                        let s = (profileData.playerStatus || '').trim();
                        if (!s || s === 'offline') s = 'inLobby';
                        // If viewer is in a lobby with the target, show "In your Lobby"
                        const viewerLobby = (this.props.catalog && this.props.catalog.currentLobby) || null;
                        const members = (viewerLobby && viewerLobby.members) || [];
                        const isSameLobby = members.some(m => String(m._id) === String(profileData._id));
                        if (isSameLobby && (s === 'inPrivateLobby' || s === 'inYourLobby')) {
                          return 'inYourLobby';
                        }
                        return s;
                      }).call(this)}
                      sendRequestFriend={this.handleSendRequestFriendFromProfilePage}
                      cancelFriendRequest={this.handleCancelFriendRequestFromProfilePage}
                      acceptFriendRequest={this.handleAcceptFriendRequestFromProfilePage}
                      dismissFriendRequest={this.handleDismissFriendRequestFromProfilePage}
                      unfriend={this.handleUnfriendFromProfilePage}
                      friendRequestSuccess={friendRequestSuccess}
                      friendRequestError={friendRequestError}
                      isFriendRequestSubmitting={this.state.friendRequestSubmittingSteamId === profileSteamId}
                      friendshipStatus={relationship}
                    />
                  )}
                  
                  {/* Profile Tabs */}
                  <ProfileTabs 
                    value={this.state.selectedTab}
                    onChange={this.handleTabChange}
                    userId={profileData?._id}
                  />

                  {/* Tab Panels */}
                  <TabPanel value={this.state.selectedTab} index={0}>
                    <ProfileSummaryTab
                      last25MatchData={last25MatchData}
                      matchSummary={matchSummary}
                      recentStats={recentStats}
                      rating3={profileData?.rating3}
                      sortOptions={sortOptions}
                      hasPlayedAtLeastOneMatch={hasPlayedAtLeastOneMatch}
                      isLoggedIn={!!user}
                    />
                  </TabPanel>

                  <TabPanel value={this.state.selectedTab} index={1}>
                    <ProfileMatchHistoryTab
                      last25MatchData={last25MatchData}
                      matchSummary={matchSummary}
                      sortOptions={sortOptions}
                      hasPlayedAtLeastOneMatch={hasPlayedAtLeastOneMatch}
                      onSortChange={this.handleSortChange}
                      isLoggedIn={!!user}
                    />
                  </TabPanel>

                  <TabPanel value={this.state.selectedTab} index={2}>
                    <ProfileStatsTab
                      mapsStats={mapsStats}
                      hasPlayedAtLeastOneMatch={hasPlayedAtLeastOneMatch}
                    />
                  </TabPanel>

                  <TabPanel value={this.state.selectedTab} index={3}>
                    <ProfileFriendsTab
                      isOwnProfile={profileData._id === user._id}
                      userId={profileData?._id}
                    />
                  </TabPanel>

                  <div className={classes.vspacer10} />
                  <div className={classes.vspacer10} />
                </>
              )}
            </>
          )}
          </Paper>
        </div>
      </PageShell>
    );
  }
}

ProfilePage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(ProfilePage);
